/** TanStack Query hooks for hosted static sites CRUD */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, API_BASE } from '../lib/api-client'

/** Source package chip embedded in site list rows (GH-259). */
export interface SiteSourcePackage {
  id: string
  name: string
}

export interface HostedSite {
  id: string
  url: string
  uploadedBy: string
  filename: string
  siteType: 'html' | 'zip'
  entryPath: string
  totalSizeBytes: number
  fileCount: number
  description: string | null
  summary: string | null
  status: 'processing' | 'ready' | 'failed'
  createdAt: number
  updatedAt: number
  /** Present when the site was linked to an onboarding package (GH-259). */
  sourcePackage?: SiteSourcePackage | null
}

interface ListSitesResponse {
  sites: HostedSite[]
  total: number
}

export function useHostedSites(search?: string) {
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  return useQuery<ListSitesResponse>({
    queryKey: ['hosted-sites', search],
    queryFn: () => apiClient.get<ListSitesResponse>(`/api/sites?${params}`),
  })
}

export function useUploadSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, description, autoSummary }: { file: File; description?: string; autoSummary?: boolean }) => {
      const formData = new FormData()
      formData.append('file', file)
      if (description) formData.append('description', description)
      if (autoSummary) formData.append('autoSummary', 'true')

      const res = await fetch(`${API_BASE}/api/sites`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Upload failed' }))
        throw new Error((data as { error?: string }).error ?? 'Upload failed')
      }
      return res.json() as Promise<{ id: string; url: string; filename: string; fileCount: number; totalSizeBytes: number }>
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hosted-sites'] }),
  })
}

export function useDeleteSite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete<{ deleted: boolean }>(`/api/sites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hosted-sites'] }),
  })
}
